import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Info, Lock, Pencil, Target } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import type { ProductTargetingLimits } from '@/services/lineItems/productConstraints'
import {
  TARGETING_TYPES,
  countTargetingTypes,
  targetingType,
  typesInUse,
  type Targeting,
  type TargetingTypeId,
} from '@/services/targeting/targeting'
import { ChipRow } from '../ChipRow'
import { SwitchField } from '../SwitchField'
import { TargetingDialog } from './TargetingDialog'
import { segmentIdsIn, useSegmentNames } from '@/hooks/useSegmentNames'
import { summarizeTargeting } from './targetingSummary'

/** Types the editor does not cover, and the field that holds each one. */
const UNEDITABLE: { key: keyof Targeting; label: string }[] = [
  { key: 'geospatialTargets', label: 'geospatial' },
  { key: 'similarKeywordTargets', label: 'similarKeyword' },
  { key: 'thirdPartyAudienceTargets', label: 'thirdPartyAudience' },
  { key: 'weatherTargets', label: 'weather' },
]

export interface TargetingCardProps {
  targeting: Targeting
  onChange: (targeting: Targeting) => void
  hiddenTypes?: TargetingTypeId[]
  disabled?: boolean
  /**
   * Why the targeting can no longer be changed. When set, the card is read-only
   * and says so.
   */
  lockedReason?: string
  /** The booked marketplace product's rules, when one is booked. */
  product?: ProductTargetingLimits & { name: string }
  /**
   * True to save with `ignoreProduct`, which stops the server re-imposing the
   * product's targeting and lets the line item target anything.
   */
  ignoreProduct?: boolean
  onIgnoreProductChange?: (ignoreProduct: boolean) => void
  /**
   * Called once the applied targeting has been handed to the form, for a page
   * that saves there and then rather than leaving it in the save bar.
   */
  onApplied?: () => void
}

/** What a line item targets, with the editor a click away. */
export function TargetingCard({
  targeting,
  onChange,
  hiddenTypes = [],
  disabled: disabledProp,
  lockedReason,
  product,
  ignoreProduct = false,
  onIgnoreProductChange,
  onApplied,
}: TargetingCardProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const disabled = disabledProp || !!lockedReason

  // Segment targeting can arrive as bare ids, which read as nothing useful in
  // a summary chip — so the names are filled in before the chips are built.
  const { inTargeting } = useSegmentNames(segmentIdsIn(targeting))
  const lines = summarizeTargeting(inTargeting(targeting), t)
  const carried = UNEDITABLE.filter(({ key }) => {
    const value = targeting[key]
    return Array.isArray(value) ? value.length > 0 : !!value
  })

  // While the product is in force, offering a type the server would discard on
  // save is worse than not offering it — so only what survives is editable:
  // the product's own types, the ones it marks optional, and whatever the line
  // item already carries.
  const inForce = !!product && !ignoreProduct
  const allowed = inForce
    ? new Set<TargetingTypeId>([
        ...product.mandatoryTypes,
        ...product.optionalTypes,
        ...typesInUse(targeting),
      ])
    : null
  // Whatever the line item already targets stays listed, even a type the
  // network or product would not offer — hiding it would leave targeting in
  // force that can be neither seen nor removed in the editor.
  const inUse = new Set(typesInUse(targeting))
  const hidden = [
    ...hiddenTypes,
    ...(allowed ? TARGETING_TYPES.filter((def) => !allowed.has(def.id)).map((def) => def.id) : []),
  ].filter((id) => !inUse.has(id))

  return (
    <Card tone="delivery">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CardIcon icon={Target} />
          {t('targeting.title')}
          {lines.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">
              ({t('targeting.typeCount', { count: countTargetingTypes(targeting) })})
            </span>
          )}
        </CardTitle>
        <CardAction>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            <Pencil className="size-3.5" />
            {t('common.edit')}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {lockedReason && (
          <Alert>
            <Lock />
            <AlertDescription>{lockedReason}</AlertDescription>
          </Alert>
        )}

        {lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('targeting.untargeted')}</p>
        ) : (
          <div className="divide-y divide-dashed">
            {lines.map((line) => (
              <ChipRow
                key={line.id}
                label={line.label}
                icon={targetingType(line.id).icon}
                chips={line.chips}
                className="first:pt-0 last:pb-0"
              >
                {inForce && product.mandatoryTypes.includes(line.id) && (
                  <Badge variant="blue" className="font-normal">
                    {t('targeting.product.fromProduct')}
                  </Badge>
                )}
              </ChipRow>
            ))}
          </div>
        )}

        {carried.length > 0 && (
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            {t('targeting.carriedTypes', {
              types: carried.map(({ label }) => t(`targeting.types.${label}.plural`)).join(', '),
            })}
          </p>
        )}

        {product && onIgnoreProductChange && (
          <SwitchField
            label={t('targeting.product.ignoreLabel')}
            tooltip={t('targeting.product.ignoreHelp')}
            checked={ignoreProduct}
            onCheckedChange={onIgnoreProductChange}
            disabled={disabled}
          />
        )}
      </CardContent>

      <TargetingDialog
        open={open}
        onOpenChange={setOpen}
        targeting={targeting}
        onApply={(next) => {
          onChange(next)
          // The form's own state settles first, so the save reads the
          // targeting that was just applied rather than the one before it.
          if (onApplied) queueMicrotask(onApplied)
        }}
        hiddenTypes={hidden}
      />
    </Card>
  )
}
